// client/pages/P2P.tsx
// Mobile: tabs (Buy / Sell / Orders) with theme tokens
// Desktop: black page bg, 3-column Sell layout
// Chat + in-app notifications + live USDT/PKR rate

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Loader2,
  ShieldCheck,
  AlertTriangle,
  Copy,
  Check,
  X,
  Plus,
  ArrowLeft,
  Clock,
  Bell,
  MessageSquare,
  Send,
  RefreshCw,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface P2PAd {
  id: string;
  sellerEmail: string;
  sellerSolanaAddress: string;
  usdtAmount: number;
  pkrRate: number;
  minLimit: number;
  maxLimit: number;
  paymentMethod: string;
  status: "pending_lock" | "open" | "closed" | "failed";
  createdAt: number;
  lockTxHash?: string;
}

interface P2POrder {
  id: string;
  adId: string;
  sellerEmail: string;
  sellerSolanaAddress: string;
  buyerEmail: string;
  buyerSolanaAddress: string;
  usdtAmount: number;
  pkrRate: number;
  pkrTotal: number;
  status: "awaiting_payment" | "buyer_paid" | "released" | "cancelled";
  paymentMethod: string;
  createdAt: number;
  buyerPaidAt?: number;
  releasedAt?: number;
  releaseTxId?: string;
  refundTxId?: string;
  cancelledAt?: number;
  cancelledBy?: string;
}

interface P2PChatMessage {
  id: string;
  from: string;
  role: "buyer" | "seller";
  text: string;
  createdAt: number;
}

interface P2PNotification {
  id: string;
  orderId: string;
  type: "new_message" | "buyer_paid" | "released" | "cancelled" | "ad_created";
  title: string;
  body: string;
  createdAt: number;
  read: boolean;
}

const shorten = (addr?: string) => {
  if (!addr) return "";
  if (addr.length <= 14) return addr;
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
};

export default function P2P() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [user, setUser] = useState<any>(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [activeTab, setActiveTab] = useState<"buy" | "sell" | "orders">("buy");
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 1024 : false
  );

  // Ad / order lists
  const [ads, setAds] = useState<P2PAd[]>([]);
  const [myOrders, setMyOrders] = useState<P2POrder[]>([]);

  // Buy flow
  const [selectedAd, setSelectedAd] = useState<P2PAd | null>(null);
  const [amount, setAmount] = useState("");

  // Sell / create-ad flow
  const [showCreateAd, setShowCreateAd] = useState(false);
  const [adAmount, setAdAmount] = useState("");
  const [adRate, setAdRate] = useState("");
  const [adMin, setAdMin] = useState("");
  const [adMax, setAdMax] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Bank Transfer");
  const [password, setPassword] = useState("");
  const [backupFile, setBackupFile] = useState("");
  const [creatingAd, setCreatingAd] = useState(false);

  // Order actions
  const [releasing, setReleasing] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);

  // Clipboard feedback
  const [copied, setCopied] = useState<string | null>(null);

  // ─── Live USDT/PKR rate (from your existing /api/price/usdt-pkr) ───
  const [p2pRate, setP2pRate] = useState<number | null>(null);
  const [p2pRateSource, setP2pRateSource] = useState<string>("");
  const [p2pRateLoading, setP2pRateLoading] = useState(false);
  const [p2pRateUpdatedAt, setP2pRateUpdatedAt] = useState<number>(0);

  // ─── Chat state ───────────────────────────────────────────
  const [chatOpenFor, setChatOpenFor] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<Record<string, P2PChatMessage[]>>({});
  const [chatDrafts, setChatDrafts] = useState<Record<string, string>>({});
  const [chatSending, setChatSending] = useState<string | null>(null);
  const chatScrollRef = useRef<Record<string, HTMLDivElement | null>>({});

  // ─── Notification state ───────────────────────────────────
  const [notifications, setNotifications] = useState<P2PNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const knownNotifIdsRef = useRef<Set<string>>(new Set());
  const notifPanelRef = useRef<HTMLDivElement | null>(null);

  const copy = (text: string, field: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(field);
      setTimeout(() => setCopied(null), 1500);
    });
  };

  // ─── Responsive breakpoint ────────────────────────────────
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ─── Load session ─────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("/api/auth", {
          method: "POST",
          credentials: "include",
        });
        const data = await res.json();
        if (data.success && data.user) setUser(data.user);
        else navigate("/login");
      } catch {
        navigate("/login");
      } finally {
        setLoadingUser(false);
      }
    })();
  }, [navigate]);

  // ─── Loaders ──────────────────────────────────────────────
  const loadAds = useCallback(async () => {
    try {
      const res = await fetch("/api/p2p?action=list-ads", { credentials: "include" });
      const data = await res.json();
      if (data.success) setAds(data.ads || []);
    } catch {}
  }, []);

  const loadOrders = useCallback(async () => {
    try {
      const res = await fetch("/api/p2p?action=my-orders", { credentials: "include" });
      const data = await res.json();
      if (data.success) setMyOrders(data.orders || []);
    } catch {}
  }, []);

  const loadNotifications = useCallback(async () => {
    try {
      const res = await fetch("/api/p2p?action=list-notifications", {
        credentials: "include",
      });
      const data = await res.json();
      if (data.success) {
        const list: P2PNotification[] = data.notifications || [];
        for (const n of list) {
          if (!n.read && !knownNotifIdsRef.current.has(n.id)) {
            toast({ title: n.title, description: n.body });
          }
          knownNotifIdsRef.current.add(n.id);
        }
        setNotifications(list);
        setUnreadCount(data.unreadCount || 0);
      }
    } catch {}
  }, [toast]);

  useEffect(() => {
    if (!user) return;
    loadAds();
    loadOrders();
    loadNotifications();
    const t = setInterval(() => {
      loadAds();
      loadOrders();
      loadNotifications();
    }, 10000);
    return () => clearInterval(t);
  }, [user, loadAds, loadOrders, loadNotifications]);

  // ─── Fetch live USDT/PKR rate from existing endpoint ──────
  const loadP2pRate = useCallback(async () => {
    setP2pRateLoading(true);
    try {
      const res = await fetch("/api/price/usdt-pkr", { credentials: "include" });
      const data = await res.json();
      if (data?.success && Number.isFinite(data.price) && data.price > 0) {
        setP2pRate(data.price);
        setP2pRateSource(data.source || "");
        setP2pRateUpdatedAt(data.timestamp || Date.now());
      }
    } catch {
      // Silent — keep last known rate
    } finally {
      setP2pRateLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    loadP2pRate();
    const t = setInterval(loadP2pRate, 60000);
    return () => clearInterval(t);
  }, [user, loadP2pRate]);

  // Prefill ad rate when the create form opens
  useEffect(() => {
    if (showCreateAd && !adRate && p2pRate) {
      setAdRate(String(Math.round(p2pRate * 100) / 100));
    }
  }, [showCreateAd, p2pRate, adRate]);

  // ─── Close notif panel on outside click ───────────────────
  useEffect(() => {
    if (!showNotifPanel) return;
    const handler = (e: MouseEvent) => {
      if (notifPanelRef.current && !notifPanelRef.current.contains(e.target as Node)) {
        setShowNotifPanel(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showNotifPanel]);

  // ─── Chat: fetch messages while open ──────────────────────
  useEffect(() => {
    if (!chatOpenFor) return;
    const orderId = chatOpenFor;

    const fetchMessages = async () => {
      try {
        const res = await fetch(`/api/p2p?action=get-messages&orderId=${orderId}`, {
          credentials: "include",
        });
        const data = await res.json();
        if (data.success) {
          setChatMessages((prev) => ({ ...prev, [orderId]: data.messages || [] }));
          const el = chatScrollRef.current[orderId];
          if (el) setTimeout(() => (el.scrollTop = el.scrollHeight), 30);
        }
      } catch {}
    };

    fetchMessages();
    const t = setInterval(fetchMessages, 5000);
    return () => clearInterval(t);
  }, [chatOpenFor]);

  // ─── Notification actions ─────────────────────────────────
  const handleMarkRead = async (notifId?: string, all?: boolean) => {
    try {
      const res = await fetch("/api/p2p?action=mark-read", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ notifId, all: !!all }),
      });
      const data = await res.json();
      if (data.success) {
        setNotifications(data.notifications || []);
        setUnreadCount(data.unreadCount || 0);
      }
    } catch {}
  };

  const handleNotifClick = (n: P2PNotification) => {
    handleMarkRead(n.id);
    setShowNotifPanel(false);
    setActiveTab("orders");
    if (n.type === "new_message") {
      setChatOpenFor(n.orderId);
    }
  };

  // ─── Chat actions ─────────────────────────────────────────
  const handleSendMessage = async (orderId: string) => {
    const draft = (chatDrafts[orderId] || "").trim();
    if (!draft) return;
    setChatSending(orderId);
    try {
      const res = await fetch("/api/p2p?action=send-message", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderId, text: draft }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to send");
      setChatMessages((prev) => ({ ...prev, [orderId]: data.messages || [] }));
      setChatDrafts((prev) => ({ ...prev, [orderId]: "" }));
      const el = chatScrollRef.current[orderId];
      if (el) setTimeout(() => (el.scrollTop = el.scrollHeight), 30);
    } catch (e: any) {
      toast({ title: "SEND FAILED", description: e.message, variant: "destructive" });
    } finally {
      setChatSending(null);
    }
  };

  // ─── Create ad ────────────────────────────────────────────
  const handleCreateAd = async () => {
    if (!password || !backupFile) {
      toast({
        title: "PASSWORD & BACKUP REQUIRED",
        description: "Same credentials you use for withdrawals.",
        variant: "destructive",
      });
      return;
    }
    setCreatingAd(true);
    try {
      const res = await fetch("/api/p2p?action=create-ad", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          usdtAmount: parseFloat(adAmount),
          pkrRate: parseFloat(adRate),
          minLimit: parseFloat(adMin),
          maxLimit: parseFloat(adMax),
          paymentMethod,
          password,
          backupFile,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to create ad");

      toast({ title: "LOCKING USDT…", description: "Waiting for on-chain confirmation" });

      let confirmed = false;
      for (let i = 0; i < 20; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const c = await fetch("/api/p2p?action=confirm-lock", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ adId: data.adId, txId: data.txId }),
        });
        const cj = await c.json();
        if (cj.status === "open") {
          confirmed = true;
          break;
        }
        if (cj.status === "failed") throw new Error("Lock transaction failed on-chain");
      }
      if (!confirmed) throw new Error("Lock confirmation timed out");

      toast({ title: "AD CREATED", description: `${adAmount} USDT locked in escrow` });
      setShowCreateAd(false);
      setAdAmount("");
      setAdRate("");
      setAdMin("");
      setAdMax("");
      setPassword("");
      setBackupFile("");
      loadAds();
      loadNotifications();
    } catch (e: any) {
      toast({ title: "FAILED", description: e.message, variant: "destructive" });
    } finally {
      setCreatingAd(false);
    }
  };

  const handleTakeOrder = async () => {
    if (!selectedAd) return;
    try {
      const res = await fetch("/api/p2p?action=take-order", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          adId: selectedAd.id,
          usdtAmount: parseFloat(amount),
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to take order");
      toast({
        title: "ORDER CREATED",
        description: `Send PKR ${data.order.pkrTotal.toFixed(0)} to seller, then click BUYER: I'VE PAID`,
      });
      setSelectedAd(null);
      setAmount("");
      setActiveTab("orders");
      loadOrders();
      loadNotifications();
    } catch (e: any) {
      toast({ title: "FAILED", description: e.message, variant: "destructive" });
    }
  };

  const handleBuyerPaid = async (orderId: string) => {
    try {
      const res = await fetch("/api/p2p?action=buyer-paid", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderId }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed");
      toast({
        title: "WAITING FOR SELLER",
        description: "Seller will release USDT after checking PKR",
      });
      loadOrders();
    } catch (e: any) {
      toast({ title: "FAILED", description: e.message, variant: "destructive" });
    }
  };

  const handleRelease = async (orderId: string) => {
    if (!confirm("RELEASE USDT? This is irreversible once confirmed on-chain.")) return;
    setReleasing(orderId);
    try {
      const res = await fetch("/api/p2p?action=release", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderId }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Release failed");
      toast({ title: "RELEASE SUBMITTED", description: "USDT will arrive in buyer's wallet shortly" });
      loadOrders();
      loadNotifications();
    } catch (e: any) {
      toast({ title: "FAILED", description: e.message, variant: "destructive" });
    } finally {
      setReleasing(null);
    }
  };

  const handleCancel = async (orderId: string) => {
    if (!confirm("Cancel this order? USDT will be refunded to the seller.")) return;
    setCancelling(orderId);
    try {
      const res = await fetch("/api/p2p?action=cancel", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderId }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Cancel failed");
      toast({ title: "CANCELLED", description: "USDT refunded to seller" });
      loadOrders();
      loadNotifications();
    } catch (e: any) {
      toast({ title: "FAILED", description: e.message, variant: "destructive" });
    } finally {
      setCancelling(null);
    }
  };

  // ─── Helpers ──────────────────────────────────────────────
  const isSeller = (order: P2POrder) => order.sellerEmail === user?.email;
  const isBuyer = (order: P2POrder) => order.buyerEmail === user?.email;

  const orderStatusBadge = (status: P2POrder["status"]) => {
    const map: Record<P2POrder["status"], string> = {
      awaiting_payment: "bg-yellow-500/20 text-yellow-500",
      buyer_paid: "bg-blue-500/20 text-blue-500",
      released: "bg-green-500/20 text-green-500",
      cancelled: "bg-red-500/20 text-red-500",
    };
    const label: Record<P2POrder["status"], string> = {
      awaiting_payment: "AWAITING PAYMENT",
      buyer_paid: "BUYER PAID",
      released: "RELEASED",
      cancelled: "CANCELLED",
    };
    return (
      <span className={`text-[9px] px-2 py-0.5 rounded font-bold uppercase ${map[status]}`}>
        {label[status]}
      </span>
    );
  };

  const formatTime = (ts: number) => {
    const diff = Math.floor((Date.now() - ts) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  if (loadingUser) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-foreground" />
      </div>
    );
  }

  if (!user) return null;
    return (
    <div
      className={
        isDesktop
          ? "min-h-screen bg-black text-white px-3 py-4 pb-24"
          : "min-h-screen bg-background text-foreground px-3 py-4 max-w-2xl mx-auto pb-24"
      }
      style={
        isDesktop
          ? {
              backgroundColor: "#000000",
              color: "#ffffff",
              maxWidth: "1400px",
              margin: "0 auto",
            }
          : undefined
      }
    >
      {/* ─── Header with notification bell ─── */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
         
          <h1
            className={`text-xl font-bold uppercase ${
              isDesktop ? "text-white" : "text-foreground"
            }`}
          >
currency exchange          </h1>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-[8px] bg-yellow-400/20 text-yellow-400 px-2 py-1 rounded-md uppercase font-bold">
            USDT / PKR
          </span>

          {/* Notification bell */}
          <div className="relative" ref={notifPanelRef}>
            <button
              onClick={() => setShowNotifPanel((v) => !v)}
              className={`relative p-2 rounded-md transition-colors ${
                isDesktop
                  ? "hover:bg-zinc-800 text-white"
                  : "hover:bg-secondary text-foreground"
              }`}
              aria-label="Notifications"
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>

            {showNotifPanel && (
              <div
                className={`absolute right-0 top-full mt-1 z-50 w-80 max-h-96 overflow-y-auto rounded-lg border shadow-xl ${
                  isDesktop
                    ? "bg-zinc-950 border-zinc-800 text-white"
                    : "bg-card border-border text-foreground"
                }`}
              >
                <div
                  className={`flex items-center justify-between px-3 py-2 border-b ${
                    isDesktop ? "border-zinc-800" : "border-border"
                  }`}
                >
                  <p className="text-[10px] font-bold uppercase">
                    Notifications
                  </p>
                  {unreadCount > 0 && (
                    <button
                      onClick={() => handleMarkRead(undefined, true)}
                      className="text-[9px] uppercase text-yellow-400 hover:text-yellow-500"
                    >
                      Mark all read
                    </button>
                  )}
                </div>

                {notifications.length === 0 && (
                  <p
                    className={`text-center text-[10px] uppercase py-6 ${
                      isDesktop ? "text-zinc-400" : "text-muted-foreground"
                    }`}
                  >
                    No notifications yet
                  </p>
                )}

                {notifications.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => handleNotifClick(n)}
                    className={`w-full text-left px-3 py-2 border-b transition-colors ${
                      isDesktop
                        ? "border-zinc-800 hover:bg-zinc-900"
                        : "border-border hover:bg-secondary"
                    } ${!n.read ? "bg-yellow-500/5" : ""}`}
                  >
                    <div className="flex items-start gap-2">
                      {!n.read && (
                        <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 mt-1.5 shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-bold uppercase">
                          {n.title}
                        </p>
                        <p
                          className={`text-[10px] mt-0.5 break-words ${
                            isDesktop ? "text-zinc-400" : "text-muted-foreground"
                          }`}
                        >
                          {n.body}
                        </p>
                        <p
                          className={`text-[8px] uppercase mt-1 ${
                            isDesktop ? "text-zinc-500" : "text-muted-foreground/60"
                          }`}
                        >
                          {formatTime(n.createdAt)}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── Live USDT/PKR rate banner ─── */}
            {/* ─── Live rate + wallet (single card, two columns) ─── */}
      <div
        className={`mb-4 rounded-lg p-3 grid ${
          user?.wallets?.solana
            ? "grid-cols-2 divide-x"
            : "grid-cols-1"
        } ${
          isDesktop
            ? "bg-zinc-950 border border-zinc-800 divide-zinc-800"
            : "bg-transparent border border-border divide-border"
        }`}
      >
        {/* Left column — Live rate */}
        <div className="flex items-center justify-between pr-2 min-w-0">
          <div className="min-w-0">
            <p
              className={`text-[9px] uppercase tracking-wider ${
                isDesktop ? "text-zinc-400" : "text-muted-foreground"
              }`}
            >
              Live USDT / PKR
            </p>
            <div className="flex items-baseline gap-2">
              <p
                className={`text-sm font-bold ${
                  isDesktop ? "text-white" : "text-foreground"
                }`}
              >
                {p2pRateLoading && !p2pRate
                  ? "Loading…"
                  : p2pRate
                  ? `${p2pRate.toLocaleString()}`
                  : "—"}
              </p>
              {p2pRateSource && (
                <span
                  className={`text-[8px] uppercase ${
                    isDesktop ? "text-zinc-500" : "text-muted-foreground/70"
                  }`}
                >
{p2pRateSource === "binance_p2p" ||
p2pRateSource === "binance_p2p_proxy" ||
p2pRateSource === "p2p_army_binance"
  ? "Binance"
  : p2pRateSource === "p2p_army"
  ? "Aggregator"
  : p2pRateSource === "bank_rate"
  ? "Bank"
  : p2pRateSource}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={loadP2pRate}
            disabled={p2pRateLoading}
            className={`p-1 shrink-0 ${
              isDesktop
                ? "text-zinc-400 hover:text-white"
                : "text-muted-foreground hover:text-foreground"
            }`}
            aria-label="Refresh rate"
            title={
              p2pRateUpdatedAt
                ? `Updated ${formatTime(p2pRateUpdatedAt)}`
                : "Refresh rate"
            }
          >
            <RefreshCw
              className={`w-3 h-3 ${p2pRateLoading ? "animate-spin" : ""}`}
            />
          </button>
        </div>

        {/* Right column — Wallet address */}
        {user?.wallets?.solana && (
          <div className="flex items-center justify-between pl-2 min-w-0">
            <div className="min-w-0">
              <p
                className={`text-[9px] uppercase tracking-wider ${
                  isDesktop ? "text-zinc-400" : "text-muted-foreground"
                }`}
              >
                Your Solana Wallet
              </p>
              <p
                className={`text-[10px] font-mono truncate ${
                  isDesktop ? "text-white" : "text-foreground"
                }`}
              >
                {shorten(user.wallets.solana)}
              </p>
            </div>
            <button
              onClick={() => copy(user.wallets.solana, "my-wallet")}
              className={`p-1 shrink-0 ${
                isDesktop
                  ? "text-zinc-400 hover:text-white"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {copied === "my-wallet" ? (
                <Check className="w-3 h-3 text-green-500" />
              ) : (
                <Copy className="w-3 h-3" />
              )}
            </button>
          </div>
        )}
      </div>
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
        <TabsList className="w-full grid grid-cols-3 mb-4">
          <TabsTrigger value="buy" className="text-[11px] uppercase">
            Buy usdt
          </TabsTrigger>
          <TabsTrigger value="sell" className="text-[11px] uppercase">
            Sell usdt
          </TabsTrigger>
          <TabsTrigger value="orders" className="text-[11px] uppercase">
            Orders ({myOrders.length})
          </TabsTrigger>
        </TabsList>

        {/* ─── BUY TAB ─── */}
        <TabsContent value="buy" className="space-y-3">
          {!user?.wallets?.solana && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
              <p className="text-[10px] text-red-400 uppercase">
                No Solana wallet found. Add one to buy USDT.
              </p>
            </div>
          )}

          {ads.length === 0 && (
            <div
              className={`p-6 text-center rounded-lg ${
                isDesktop
                  ? "bg-zinc-950 border border-zinc-800"
                  : "bg-transparent border border-border"
              }`}
            >
              <p
                className={`text-[10px] uppercase ${
                  isDesktop ? "text-zinc-400" : "text-muted-foreground"
                }`}
              >
                No sell ads available right now.
              </p>
            </div>
          )}

          {ads.map((ad) => (
            <div
              key={ad.id}
              onClick={() => setSelectedAd(ad)}
              className={`rounded-lg p-3 cursor-pointer transition-colors border ${
                selectedAd?.id === ad.id
                  ? "border-yellow-400"
                  : isDesktop
                  ? "bg-zinc-950 border-zinc-800 hover:border-zinc-600"
                  : "bg-transparent border-border hover:border-foreground/40"
              }`}
            >
              <div className="flex justify-between items-baseline mb-1">
                <span className="text-sm font-bold text-yellow-400">
                  {ad.pkrRate.toLocaleString()} PKR
                </span>
                <span
                  className={`text-[9px] uppercase ${
                    isDesktop ? "text-zinc-400" : "text-muted-foreground"
                  }`}
                >
                  {ad.paymentMethod}
                </span>
              </div>
              <div
                className={`flex justify-between text-[10px] uppercase ${
                  isDesktop ? "text-zinc-400" : "text-muted-foreground"
                }`}
              >
                <span>Avail: {ad.usdtAmount.toFixed(2)} USDT</span>
                <span>
                  Limit: {ad.minLimit}-{ad.maxLimit} USDT
                </span>
              </div>
              <div
                className={`flex justify-between text-[9px] mt-1 ${
                  isDesktop ? "text-zinc-400" : "text-muted-foreground"
                }`}
              >
                <span className="truncate">
                  Seller: {shorten(ad.sellerSolanaAddress)}
                </span>
                <div className="flex items-center gap-2 shrink-0">
                  {p2pRate && p2pRate > 0 && (
                    <span
                      className={`uppercase font-bold ${
                        ad.pkrRate <= p2pRate
                          ? "text-green-500"
                          : "text-red-400"
                      }`}
                    >
                      {ad.pkrRate <= p2pRate ? "GOOD" : "HIGH"} (
                      {(((ad.pkrRate - p2pRate) / p2pRate) * 100).toFixed(1)}%)
                    </span>
                  )}
                  {ad.status === "open" && (
                    <span className="text-green-500 uppercase font-bold">
                      OPEN
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}

          {selectedAd && (
            <div
              className={`rounded-lg p-3 space-y-2 border ${
                isDesktop
                  ? "bg-zinc-950 border-yellow-400/40"
                  : "bg-transparent border-yellow-400/40"
              }`}
            >
              <p
                className={`text-[10px] uppercase ${
                  isDesktop ? "text-zinc-400" : "text-muted-foreground"
                }`}
              >
                How much USDT do you want to buy? ({selectedAd.minLimit}-
                {selectedAd.maxLimit})
              </p>
              <Input
                type="number"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className={`text-lg font-bold ${
                  isDesktop
                    ? "bg-zinc-900 border-zinc-800 text-white"
                    : "bg-background"
                }`}
              />
              {amount && parseFloat(amount) > 0 && (
                <div
                  className={`rounded p-2 space-y-1 ${
                    isDesktop
                      ? "bg-zinc-900 border border-zinc-800"
                      : "bg-background border border-border"
                  }`}
                >
                  <div className="flex justify-between text-[10px]">
                    <span
                      className={`uppercase ${
                        isDesktop ? "text-zinc-400" : "text-muted-foreground"
                      }`}
                    >
                      You pay
                    </span>
                    <span className="font-bold text-yellow-400">
                      {(parseFloat(amount) * selectedAd.pkrRate).toLocaleString()}{" "}
                      PKR
                    </span>
                  </div>
                  <div className="flex justify-between text-[10px]">
                    <span
                      className={`uppercase ${
                        isDesktop ? "text-zinc-400" : "text-muted-foreground"
                      }`}
                    >
                      You receive
                    </span>
                    <span
                      className={`font-bold ${
                        isDesktop ? "text-white" : "text-foreground"
                      }`}
                    >
                      {parseFloat(amount).toFixed(2)} USDT
                    </span>
                  </div>
                </div>
              )}
              <Button
                onClick={handleTakeOrder}
                disabled={
                  !amount || parseFloat(amount) <= 0 || !user?.wallets?.solana
                }
                className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-2.5 uppercase text-[11px]"
              >
                CONTINUE
              </Button>
            </div>
          )}
        </TabsContent>
                {/* ─── SELL TAB ─── */}
        <TabsContent value="sell" className="space-y-3">
          {/* DESKTOP 3-column layout — only when form is open */}
          {isDesktop && showCreateAd && (
            <div className="grid grid-cols-[280px_minmax(0,1fr)_360px] gap-4 items-start">
              {/* LEFT COLUMN — Sell Order Info */}
              <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-4 space-y-3">
                <h2 className="text-[11px] font-bold uppercase text-white">
                  Sell Order Info
                </h2>
                <div className="space-y-2 text-[10px]">
                  <div className="border-b border-zinc-800 pb-2">
                    <p className="text-[9px] text-zinc-400 uppercase mb-1">
                      Your Solana Wallet
                    </p>
                    <div className="flex items-center gap-1">
                      <span className="font-mono text-white text-[10px] truncate">
                        {user?.wallets?.solana || "—"}
                      </span>
                      {user?.wallets?.solana && (
                        <button
                          onClick={() =>
                            copy(user.wallets.solana, "seller-wallet")
                          }
                          className="text-zinc-400 hover:text-white shrink-0"
                        >
                          {copied === "seller-wallet" ? (
                            <Check className="w-3 h-3 text-green-500" />
                          ) : (
                            <Copy className="w-3 h-3" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="border-b border-zinc-800 pb-2">
                    <p className="text-[9px] text-zinc-400 uppercase mb-1">
                      Escrow Flow
                    </p>
                    <p className="text-[10px] text-zinc-300 uppercase leading-relaxed">
                      Your USDT is locked in a platform escrow wallet when you
                      create this ad. It leaves your personal wallet but stays
                      under platform control until a buyer takes the order and
                      you release it.
                    </p>
                  </div>
                  <div className="border-b border-zinc-800 pb-2">
                    <p className="text-[9px] text-zinc-400 uppercase mb-1">
                      Refund Policy
                    </p>
                    <p className="text-[10px] text-zinc-300 uppercase leading-relaxed">
                      If a buyer cancels before paying, USDT is refunded to you.
                      If you cancel after buyer paid, USDT is refunded to you
                      pending admin review.
                    </p>
                  </div>
                  <div>
                    <p className="text-[9px] text-zinc-400 uppercase mb-1">
                      Fees
                    </p>
                    <p className="text-[10px] text-zinc-300 uppercase leading-relaxed">
                      0% platform fee. Only Solana network fees apply.
                    </p>
                  </div>
                </div>
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-2">
                  <p className="text-[9px] text-yellow-500 uppercase font-bold">
                    ⚠ USDT cannot be pulled back once released
                  </p>
                </div>
              </div>

              {/* CENTER COLUMN — Form */}
              <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-5 space-y-3">
                <div className="flex items-center justify-between mb-1">
                  <h2 className="text-[11px] font-bold uppercase text-white">
                    Create Sell Ad
                  </h2>
                  <button
                    onClick={() => setShowCreateAd(false)}
                    className="text-zinc-400 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div>
                  <label className="text-[9px] text-zinc-400 uppercase block mb-1">
                    USDT Amount to Sell
                  </label>
                  <Input
                    type="number"
                    placeholder="100"
                    value={adAmount}
                    onChange={(e) => setAdAmount(e.target.value)}
                    className="text-sm bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-500"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-[9px] text-zinc-400 uppercase block">
                      PKR Rate per USDT
                    </label>
                    {p2pRate && (
                      <button
                        onClick={() =>
                          setAdRate(String(Math.round(p2pRate * 100) / 100))
                        }
                        className="text-[8px] uppercase text-yellow-400 hover:text-yellow-500"
                      >
                        Use live {p2pRate.toLocaleString()} PKR
                      </button>
                    )}
                  </div>
                  <Input
                    type="number"
                    placeholder="278.50"
                    value={adRate}
                    onChange={(e) => setAdRate(e.target.value)}
                    className="text-sm bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[9px] text-zinc-400 uppercase block mb-1">
                      Min Limit
                    </label>
                    <Input
                      type="number"
                      placeholder="10"
                      value={adMin}
                      onChange={(e) => setAdMin(e.target.value)}
                      className="text-sm bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-500"
                    />
                  </div>
                  <div>
                    <label className="text-[9px] text-zinc-400 uppercase block mb-1">
                      Max Limit
                    </label>
                    <Input
                      type="number"
                      placeholder="100"
                      value={adMax}
                      onChange={(e) => setAdMax(e.target.value)}
                      className="text-sm bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-[9px] text-zinc-400 uppercase block mb-1">
                    Payment Method
                  </label>
                  <select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-white text-[11px] uppercase"
                  >
                    <option value="Bank Transfer">BANK TRANSFER</option>
                    <option value="JazzCash">JAZZCASH</option>
                    <option value="Easypaisa">EASYPAISA</option>
                    <option value="Raast">RAAST</option>
                  </select>
                </div>
                <div className="border-t border-zinc-800 pt-3">
                  <p className="text-[9px] text-zinc-400 uppercase mb-2">
                    Confirm with your withdrawal password & backup file
                  </p>
                  <Input
                    type="password"
                    placeholder="PASSWORD"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="text-sm bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-500 mb-2"
                  />
                  <textarea
                    placeholder="PASTE YOUR BACKUP FILE JSON"
                    value={backupFile}
                    onChange={(e) => setBackupFile(e.target.value)}
                    className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-white text-[10px] font-mono resize-none h-20 placeholder:text-zinc-500"
                  />
                </div>
              </div>

              {/* RIGHT COLUMN — Buyer Preview */}
              <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-4 space-y-3 sticky top-4">
                <h2 className="text-[11px] font-bold uppercase text-white">
                  Buyer Preview
                </h2>
                <div className="border border-zinc-800 bg-zinc-900 rounded-lg p-3 space-y-1">
                  <div className="flex justify-between items-baseline">
                    <span className="text-sm font-bold text-yellow-400">
                      {adRate
                        ? `${parseFloat(adRate).toLocaleString()} PKR`
                        : "— PKR"}
                    </span>
                    <span className="text-[9px] text-zinc-400 uppercase">
                      {paymentMethod}
                    </span>
                  </div>
                  <div className="flex justify-between text-[10px] text-zinc-400 uppercase">
                    <span>
                      Avail:{" "}
                      {adAmount
                        ? `${parseFloat(adAmount).toFixed(2)} USDT`
                        : "— USDT"}
                    </span>
                    <span>
                      Limit: {adMin || "—"}-{adMax || "—"} USDT
                    </span>
                  </div>
                  <div className="flex justify-between text-[9px] text-zinc-400 mt-1">
                    <span className="truncate">
                      Seller: {shorten(user?.wallets?.solana) || "—"}
                    </span>
                    <span className="text-green-500 uppercase font-bold">
                      PREVIEW
                    </span>
                  </div>
                </div>
                {adAmount && adRate && (
                  <div className="border border-zinc-800 bg-zinc-900 rounded p-2">
                    <div className="flex justify-between text-[10px]">
                      <span className="text-zinc-400 uppercase">
                        Ad total value
                      </span>
                      <span className="font-bold text-yellow-400">
                        {(
                          parseFloat(adAmount) * parseFloat(adRate)
                        ).toLocaleString()}{" "}
                        PKR
                      </span>
                    </div>
                  </div>
                )}
                {p2pRate && adRate && (
                  <div className="border border-zinc-800 bg-zinc-900 rounded p-2">
                    <div className="flex justify-between text-[10px]">
                      <span className="text-zinc-400 uppercase">
                        vs Live rate
                      </span>
                      <span
                        className={`font-bold uppercase ${
                          parseFloat(adRate) <= p2pRate
                            ? "text-green-500"
                            : "text-red-400"
                        }`}
                      >
                        {parseFloat(adRate) <= p2pRate ? "GOOD" : "HIGH"} (
                        {(
                          ((parseFloat(adRate) - p2pRate) / p2pRate) *
                          100
                        ).toFixed(1)}
                        %)
                      </span>
                    </div>
                  </div>
                )}
                <div className="border-t border-zinc-800 pt-3 space-y-2">
                  <Button
                    onClick={handleCreateAd}
                    disabled={
                      creatingAd ||
                      !adAmount ||
                      !adRate ||
                      !adMin ||
                      !adMax ||
                      !password ||
                      !backupFile
                    }
                    className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-bold uppercase text-[11px] py-2.5"
                  >
                    {creatingAd ? (
                      <>
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        LOCKING…
                      </>
                    ) : (
                      "LOCK & CREATE AD"
                    )}
                  </Button>
                  <Button
                    onClick={() => setShowCreateAd(false)}
                    disabled={creatingAd}
                    variant="outline"
                    className="w-full uppercase text-[11px] border-zinc-800 text-white hover:bg-zinc-900"
                  >
                    CANCEL
                  </Button>
                </div>
                <p className="text-[9px] text-zinc-400 uppercase leading-relaxed">
                  Once locked, {adAmount || "0"} USDT leaves your wallet and is
                  held by the platform escrow. Cancel any open order to get it
                  refunded.
                </p>
              </div>
            </div>
          )}

          {/* MOBILE SELL TAB — untouched */}
          {!isDesktop && (
            <>
              {!showCreateAd && (
                <div className="bg-transparent border border-border rounded-lg p-4">
                  <p className="text-[11px] text-muted-foreground uppercase mb-3">
                    Create a sell ad to sell USDT for PKR. Your USDT is locked in
                    escrow immediately.
                  </p>
                  <Button
                    onClick={() => setShowCreateAd(true)}
                    className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-2.5 uppercase text-[11px] flex items-center justify-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    CREATE SELL AD
                  </Button>
                </div>
              )}

              {showCreateAd && (
                <div className="bg-transparent border border-yellow-400/40 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between mb-1">
                    <h2 className="text-[11px] font-bold uppercase text-foreground">
                      Create Sell Ad
                    </h2>
                    <button
                      onClick={() => setShowCreateAd(false)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div>
                    <label className="text-[9px] text-muted-foreground uppercase block mb-1">
                      USDT Amount to Sell
                    </label>
                    <Input
                      type="number"
                      placeholder="100"
                      value={adAmount}
                      onChange={(e) => setAdAmount(e.target.value)}
                      className="text-sm bg-background"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-[9px] text-muted-foreground uppercase block">
                        PKR Rate per USDT
                      </label>
                      {p2pRate && (
                        <button
                          onClick={() =>
                            setAdRate(String(Math.round(p2pRate * 100) / 100))
                          }
                          className="text-[8px] uppercase text-yellow-400 hover:text-yellow-500"
                        >
                          Use live {p2pRate.toLocaleString()}
                        </button>
                      )}
                    </div>
                    <Input
                      type="number"
                      placeholder="278.50"
                      value={adRate}
                      onChange={(e) => setAdRate(e.target.value)}
                      className="text-sm bg-background"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[9px] text-muted-foreground uppercase block mb-1">
                        Min Limit
                      </label>
                      <Input
                        type="number"
                        placeholder="10"
                        value={adMin}
                        onChange={(e) => setAdMin(e.target.value)}
                        className="text-sm bg-background"
                      />
                    </div>
                    <div>
                      <label className="text-[9px] text-muted-foreground uppercase block mb-1">
                        Max Limit
                      </label>
                      <Input
                        type="number"
                        placeholder="100"
                        value={adMax}
                        onChange={(e) => setAdMax(e.target.value)}
                        className="text-sm bg-background"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[9px] text-muted-foreground uppercase block mb-1">
                      Payment Method
                    </label>
                    <select
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground text-[11px] uppercase"
                    >
                      <option value="Bank Transfer">BANK TRANSFER</option>
                      <option value="JazzCash">JAZZCASH</option>
                      <option value="Easypaisa">EASYPAISA</option>
                      <option value="Raast">RAAST</option>
                    </select>
                  </div>
                  <div className="border-t border-border pt-3">
                    <p className="text-[9px] text-muted-foreground uppercase mb-2">
                      Confirm with your withdrawal password & backup file
                    </p>
                    <Input
                      type="password"
                      placeholder="PASSWORD"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="text-sm bg-background mb-2"
                    />
                    <textarea
                      placeholder="PASTE YOUR BACKUP FILE JSON"
                      value={backupFile}
                      onChange={(e) => setBackupFile(e.target.value)}
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground text-[10px] font-mono resize-none h-20"
                    />
                  </div>
                  {adAmount && adRate && (
                    <div className="bg-background border border-border rounded p-2">
                      <div className="flex justify-between text-[10px]">
                        <span className="text-muted-foreground uppercase">
                          Ad total value
                        </span>
                        <span className="font-bold text-yellow-400">
                          {(
                            parseFloat(adAmount) * parseFloat(adRate)
                          ).toLocaleString()}{" "}
                          PKR
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setShowCreateAd(false)}
                      disabled={creatingAd}
                      variant="outline"
                      className="flex-1 uppercase text-[11px]"
                    >
                      CANCEL
                    </Button>
                    <Button
                      onClick={handleCreateAd}
                      disabled={
                        creatingAd ||
                        !adAmount ||
                        !adRate ||
                        !adMin ||
                        !adMax ||
                        !password ||
                        !backupFile
                      }
                      className="flex-1 bg-yellow-400 hover:bg-yellow-500 text-black font-bold uppercase text-[11px]"
                    >
                      {creatingAd ? (
                        <>
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          LOCKING…
                        </>
                      ) : (
                        "LOCK & CREATE"
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* DESKTOP idle state — no form open */}
          {isDesktop && !showCreateAd && (
            <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4">
              <p className="text-[11px] text-zinc-400 uppercase mb-3">
                Create a sell ad to sell USDT for PKR. Your USDT is locked in
                escrow immediately. It can be reclaimed if the order is
                cancelled.
              </p>
              <Button
                onClick={() => setShowCreateAd(true)}
                className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-2.5 uppercase text-[11px] flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" />
                CREATE SELL AD
              </Button>
            </div>
          )}
        </TabsContent>
                {/* ─── ORDERS TAB ─── */}
        <TabsContent value="orders" className="space-y-3">
          {myOrders.length === 0 && (
            <div
              className={`p-6 text-center rounded-lg ${
                isDesktop
                  ? "bg-zinc-950 border border-zinc-800"
                  : "bg-transparent border border-border"
              }`}
            >
              <p
                className={`text-[10px] uppercase ${
                  isDesktop ? "text-zinc-400" : "text-muted-foreground"
                }`}
              >
                No orders yet.
              </p>
            </div>
          )}

          {myOrders.map((order) => {
            const seller = isSeller(order);
            const buyer = isBuyer(order);
            const isChatOpen = chatOpenFor === order.id;
            const messages = chatMessages[order.id] || [];
            const draft = chatDrafts[order.id] || "";

            return (
              <div
                key={order.id}
                className={`p-3 space-y-3 rounded-lg ${
                  isDesktop
                    ? "bg-zinc-950 border border-zinc-800"
                    : "bg-transparent border border-border"
                }`}
              >
                {/* Header */}
                <div className="flex justify-between items-start">
                  <div>
                    <p
                      className={`text-[9px] uppercase ${
                        isDesktop ? "text-zinc-400" : "text-muted-foreground"
                      }`}
                    >
                      Order #{order.id.slice(-6)}
                    </p>
                    <p
                      className={`text-sm font-bold ${
                        isDesktop ? "text-white" : "text-foreground"
                      }`}
                    >
                      {order.usdtAmount.toFixed(2)} USDT
                    </p>
                    <p className="text-[10px] text-yellow-400 font-semibold">
                      {order.pkrTotal.toLocaleString()} PKR @ {order.pkrRate}
                    </p>
                  </div>
                  {orderStatusBadge(order.status)}
                </div>

                {/* Counterparty */}
                <div
                  className={`p-2 space-y-1 rounded ${
                    isDesktop
                      ? "bg-zinc-900 border border-zinc-800"
                      : "bg-background border border-border"
                  }`}
                >
                  <div className="flex justify-between text-[10px]">
                    <span
                      className={`uppercase ${
                        isDesktop ? "text-zinc-400" : "text-muted-foreground"
                      }`}
                    >
                      {seller ? "Buyer" : "Seller"}
                    </span>
                    <div className="flex items-center gap-1">
                      <span
                        className={`font-mono ${
                          isDesktop ? "text-white" : "text-foreground"
                        }`}
                      >
                        {shorten(
                          seller
                            ? order.buyerSolanaAddress
                            : order.sellerSolanaAddress
                        )}
                      </span>
                      <button
                        onClick={() =>
                          copy(
                            seller
                              ? order.buyerSolanaAddress
                              : order.sellerSolanaAddress,
                            `addr-${order.id}`
                          )
                        }
                        className={
                          isDesktop
                            ? "text-zinc-400 hover:text-white"
                            : "text-muted-foreground hover:text-foreground"
                        }
                      >
                        {copied === `addr-${order.id}` ? (
                          <Check className="w-3 h-3 text-green-500" />
                        ) : (
                          <Copy className="w-3 h-3" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-between text-[10px]">
                    <span
                      className={`uppercase ${
                        isDesktop ? "text-zinc-400" : "text-muted-foreground"
                      }`}
                    >
                      Method
                    </span>
                    <span className={isDesktop ? "text-white" : "text-foreground"}>
                      {order.paymentMethod}
                    </span>
                  </div>
                  <div className="flex justify-between text-[10px]">
                    <span
                      className={`uppercase ${
                        isDesktop ? "text-zinc-400" : "text-muted-foreground"
                      }`}
                    >
                      Created
                    </span>
                    <span className={isDesktop ? "text-white" : "text-foreground"}>
                      {new Date(order.createdAt).toLocaleString()}
                    </span>
                  </div>
                  {order.releaseTxId && (
                    <div className="flex justify-between text-[10px]">
                      <span
                        className={`uppercase ${
                          isDesktop ? "text-zinc-400" : "text-muted-foreground"
                        }`}
                      >
                        Release Tx
                      </span>
                      <span className="font-mono text-green-500 text-[9px] truncate max-w-[150px]">
                        {order.releaseTxId}
                      </span>
                    </div>
                  )}
                  {order.refundTxId && (
                    <div className="flex justify-between text-[10px]">
                      <span
                        className={`uppercase ${
                          isDesktop ? "text-zinc-400" : "text-muted-foreground"
                        }`}
                      >
                        Refund Tx
                      </span>
                      <span className="font-mono text-blue-500 text-[9px] truncate max-w-[150px]">
                        {order.refundTxId}
                      </span>
                    </div>
                  )}
                </div>

                {/* Awaiting payment */}
                {order.status === "awaiting_payment" && (
                  <div className="space-y-2">
                    {buyer && (
                      <>
                        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-2">
                          <p className="text-[10px] text-yellow-500 uppercase font-bold">
                            Send exactly {order.pkrTotal.toLocaleString()} PKR to
                            seller via {order.paymentMethod}. Then click below.
                          </p>
                        </div>
                        <Button
                          onClick={() => handleBuyerPaid(order.id)}
                          className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-2.5 uppercase text-[11px]"
                        >
                          BUYER: I'VE PAID
                        </Button>
                        <Button
                          onClick={() => handleCancel(order.id)}
                          disabled={cancelling === order.id}
                          variant="outline"
                          className="w-full uppercase text-[10px]"
                        >
                          {cancelling === order.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            "CANCEL ORDER"
                          )}
                        </Button>
                      </>
                    )}
                    {seller && (
                      <div
                        className={`p-2 rounded ${
                          isDesktop
                            ? "bg-zinc-900 border border-zinc-800"
                            : "bg-background border border-border"
                        }`}
                      >
                        <p
                          className={`text-[10px] uppercase flex items-center gap-2 ${
                            isDesktop ? "text-zinc-400" : "text-muted-foreground"
                          }`}
                        >
                          <Clock className="w-3 h-3" />
                          Waiting for buyer to pay{" "}
                          {order.pkrTotal.toLocaleString()} PKR
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Buyer paid */}
                {order.status === "buyer_paid" && (
                  <div className="space-y-2">
                    {seller && (
                      <>
                        <div className="bg-red-500/10 border border-red-500/30 rounded p-2">
                          <p className="text-[10px] text-red-400 uppercase font-bold flex items-start gap-2">
                            <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                            Verify PKR received in your bank before releasing.
                            USDT transfer is irreversible once sent.
                          </p>
                        </div>
                        <Button
                          onClick={() => handleRelease(order.id)}
                          disabled={releasing === order.id}
                          className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-2.5 uppercase text-[11px]"
                        >
                          {releasing === order.id ? (
                            <>
                              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                              RELEASING…
                            </>
                          ) : (
                            <>
                              <ShieldCheck className="w-3 h-3 mr-1" />
                              RELEASE {order.usdtAmount} USDT
                            </>
                          )}
                        </Button>
                        <Button
                          onClick={() => handleCancel(order.id)}
                          disabled={cancelling === order.id}
                          variant="outline"
                          className="w-full uppercase text-[10px]"
                        >
                          {cancelling === order.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            "CANCEL & REFUND"
                          )}
                        </Button>
                      </>
                    )}
                    {buyer && (
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded p-2">
                        <p className="text-[10px] text-blue-400 uppercase flex items-center gap-2">
                          <Clock className="w-3 h-3" />
                          Waiting for seller to confirm and release USDT
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Released */}
                {order.status === "released" && (
                  <div className="bg-green-500/10 border border-green-500/30 rounded p-2">
                    <p className="text-[10px] text-green-400 uppercase font-bold flex items-center gap-2">
                      <ShieldCheck className="w-3 h-3" />
                      USDT released to buyer on-chain
                    </p>
                  </div>
                )}

                {/* Cancelled */}
                {order.status === "cancelled" && (
                  <div
                    className={`p-2 rounded ${
                      isDesktop
                        ? "bg-zinc-900 border border-zinc-800"
                        : "bg-background border border-border"
                    }`}
                  >
                    <p
                      className={`text-[10px] uppercase ${
                        isDesktop ? "text-zinc-400" : "text-muted-foreground"
                      }`}
                    >
                      Order cancelled — USDT refunded to seller
                    </p>
                  </div>
                )}

                {/* ─── Chat toggle ─── */}
                <div className="pt-1">
                  <button
                    onClick={() =>
                      setChatOpenFor(isChatOpen ? null : order.id)
                    }
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-md border transition-colors ${
                      isDesktop
                        ? "bg-zinc-900 border-zinc-800 text-white hover:bg-zinc-800"
                        : "bg-background border-border text-foreground hover:bg-secondary"
                    }`}
                  >
                    <span className="flex items-center gap-2 text-[10px] uppercase font-bold">
                      <MessageSquare className="w-3.5 h-3.5" />
                      Chat with {seller ? "buyer" : "seller"}
                    </span>
                    <span
                      className={`text-[9px] uppercase ${
                        isDesktop ? "text-zinc-400" : "text-muted-foreground"
                      }`}
                    >
                      {isChatOpen ? "Hide" : "Open"}
                    </span>
                  </button>
                </div>

                {/* ─── Chat panel ─── */}
                {isChatOpen && (
                  <div
                    className={`rounded-md border ${
                      isDesktop
                        ? "bg-zinc-950 border-zinc-800"
                        : "bg-card border-border"
                    }`}
                  >
                    <div
                      ref={(el) => {
                        chatScrollRef.current[order.id] = el;
                      }}
                      className="max-h-64 overflow-y-auto p-3 space-y-2"
                    >
                      {messages.length === 0 && (
                        <p
                          className={`text-center text-[10px] uppercase py-4 ${
                            isDesktop
                              ? "text-zinc-500"
                              : "text-muted-foreground"
                          }`}
                        >
                          No messages yet. Say hello.
                        </p>
                      )}
                      {messages.map((m) => {
                        const mine = m.from === user?.email;
                        return (
                          <div
                            key={m.id}
                            className={`flex ${
                              mine ? "justify-end" : "justify-start"
                            }`}
                          >
                            <div
                              className={`max-w-[75%] rounded-lg px-3 py-2 ${
                                mine
                                  ? "bg-yellow-400 text-black"
                                  : isDesktop
                                  ? "bg-zinc-800 text-white"
                                  : "bg-secondary text-foreground"
                              }`}
                            >
                              <p className="text-[9px] uppercase font-bold opacity-70 mb-0.5">
                                {m.role}
                              </p>
                              <p className="text-[11px] leading-snug break-words">
                                {m.text}
                              </p>
                              <p className="text-[8px] opacity-60 mt-1 uppercase">
                                {formatTime(m.createdAt)}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div
                      className={`flex items-center gap-2 p-2 border-t ${
                        isDesktop ? "border-zinc-800" : "border-border"
                      }`}
                    >
                      <Input
                        type="text"
                        placeholder="Type a message…"
                        value={draft}
                        onChange={(e) =>
                          setChatDrafts((prev) => ({
                            ...prev,
                            [order.id]: e.target.value,
                          }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage(order.id);
                          }
                        }}
                        className={`text-[11px] ${
                          isDesktop
                            ? "bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-500"
                            : "bg-background"
                        }`}
                      />
                      <Button
                        onClick={() => handleSendMessage(order.id)}
                        disabled={chatSending === order.id || !draft.trim()}
                        className="shrink-0 bg-yellow-400 hover:bg-yellow-500 text-black font-bold px-3 py-2"
                      >
                        {chatSending === order.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Send className="w-3.5 h-3.5" />
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </TabsContent>
      </Tabs>
    </div>
  );
}
